<script lang="ts">
  // Loan purposes editor per INTERFACE-SPECIFICATION.md §4.5
  // (Loan Purposes — Matrix View) and §6 (Detail / Edit Mode).
  //
  // The maintenance interface previously did not surface loan_purposes
  // at all in the Catalogue Detail view. PR 4.1 adds an inline editor
  // gated on proposer:purposes. The operator can edit display_name and
  // amount_range per existing purpose, remove a purpose, or add a new
  // one. The id stays locked once set, matching the spec convention
  // that purpose identifiers are stable references.
  //
  // Validation is local and conservative:
  // - Identifier uniqueness within the product.
  // - Per-purpose amount_range must fit inside the product's
  //   loan_amount_range (min >= product.min, max <= product.max,
  //   purpose.min < purpose.max).
  // - display_name is required and non-empty.
  //
  // The server-side schema validator catches additional structural
  // failures (url_slug shape, internal-only flag semantics) at
  // validate / submit time.

  import {
    stageAddPurpose,
    stageRemovePurpose,
    stageUpdatePurpose,
    unstagePurposeEdit,
    stagedEditsState
  } from '../lib/staged-edits.svelte';
  import { navigate } from '../lib/router.svelte';
  import { formatAmount } from '../lib/formatters';
  import type { ProductCatalogueFile, ProposeResponse } from '../lib/types';
  import type { LoanPurpose } from '../lib/staged-edits';
  import SubmitForReview from './SubmitForReview.svelte';
  import ValueTypeLockBanner from './ValueTypeLockBanner.svelte';

  interface Props {
    catalogue: ProductCatalogueFile;
  }

  const { catalogue }: Props = $props();

  const productRange = $derived(catalogue.loan_amount_range);
  const basePurposes = $derived((catalogue.loan_purposes ?? []) as LoanPurpose[]);

  // Apply the staged edits to a working list so the table reflects
  // what the operator has staged (added, updated, removed are all
  // visible inline with a status pill). Derived from base + staged
  // so any base movement (rare mid-session) re-flows automatically.
  interface RowView {
    purpose: LoanPurpose;
    status: 'baseline' | 'added' | 'updated' | 'removed';
    before: LoanPurpose | null;
  }

  const rows = $derived.by<RowView[]>(() => {
    const baseById = new Map(basePurposes.map((p) => [p.id, p]));
    const result: RowView[] = basePurposes.map((p) => ({
      purpose: p,
      status: 'baseline' as const,
      before: p
    }));
    for (const edit of Object.values(stagedEditsState.purpose_edits)) {
      const idx = result.findIndex((r) => r.purpose.id === edit.purpose_id);
      if (edit.action === 'add') {
        if (idx === -1) {
          result.push({ purpose: edit.staged, status: 'added', before: null });
        }
      } else if (edit.action === 'remove') {
        if (idx !== -1) {
          result[idx] = { ...result[idx]!, status: 'removed', before: edit.before };
        }
      } else if (edit.action === 'update') {
        if (idx !== -1) {
          result[idx] = { purpose: edit.staged, status: 'updated', before: edit.before };
        }
      }
    }
    return result;
  });

  // Lock when the store holds edits of a different value type.
  // Pre-empts the ValueTypeMismatchError throw with a clean
  // disabled-editor + Discard banner. Reactive on the store so a
  // discard / submit elsewhere clears the lock automatically.
  const locked = $derived(
    stagedEditsState.valueType !== null && stagedEditsState.valueType !== 'purposes'
  );

  let submitOpen = $state<boolean>(false);
  let newPurpose = $state<{ id: string; display_name: string; url_slug: string; min: string; max: string }>({
    id: '',
    display_name: '',
    url_slug: '',
    min: '',
    max: ''
  });
  let addError = $state<string | null>(null);
  let addOpen = $state<boolean>(false);

  function openSubmit() { submitOpen = true; }
  function closeSubmit() { submitOpen = false; }
  function onProposalCreated(response: ProposeResponse) {
    submitOpen = false;
    navigate(`/changes/pending-review/${response.pr_number}`);
  }

  function purposeAmountErrorMessage(purpose: LoanPurpose): string | null {
    const range = purpose.amount_range;
    if (!range) return null;
    if (!Number.isInteger(range.min) || !Number.isInteger(range.max)) {
      return 'Amount range must be whole-dollar integers.';
    }
    if (range.min < productRange.min) {
      return `Purpose minimum ($${formatAmount(range.min)}) is below the product minimum ($${formatAmount(productRange.min)}).`;
    }
    if (range.max > productRange.max) {
      return `Purpose maximum ($${formatAmount(range.max)}) exceeds the product maximum ($${formatAmount(productRange.max)}).`;
    }
    if (range.min >= range.max) {
      return 'Purpose minimum must be strictly less than the maximum.';
    }
    return null;
  }

  function updatePurposeField(row: RowView, field: 'display_name' | 'min' | 'max' | 'url_slug', raw: string) {
    if (row.status === 'removed') return;
    const current = row.purpose;
    let next: LoanPurpose;
    if (field === 'display_name') {
      next = { ...current, display_name: raw };
    } else if (field === 'url_slug') {
      next = { ...current, url_slug: raw };
    } else {
      const parsed = /^\d+$/.test(raw.trim()) ? Number(raw.trim()) : null;
      if (parsed === null) {
        // ignore non-numeric mid-edit
        return;
      }
      const existingRange = current.amount_range ?? { min: productRange.min, max: productRange.max };
      next = {
        ...current,
        amount_range: { ...existingRange, [field]: parsed }
      };
    }
    if (row.status === 'added') {
      // Repeated edits on a newly added purpose stay collapsed into
      // a single add action so the proposal carries one purpose-add
      // per id.
      stageAddPurpose(next);
    } else if (row.before) {
      stageUpdatePurpose(current.id, row.before, next);
    }
  }

  function removePurpose(row: RowView) {
    if (row.status === 'added') {
      unstagePurposeEdit(row.purpose.id);
      return;
    }
    if (!row.before) return;
    stageRemovePurpose(row.purpose.id, row.before);
  }

  function restorePurpose(row: RowView) {
    unstagePurposeEdit(row.purpose.id);
  }

  function submitNewPurpose() {
    addError = null;
    const id = newPurpose.id.trim();
    const display_name = newPurpose.display_name.trim();
    const url_slug = newPurpose.url_slug.trim();
    if (!id) {
      addError = 'Identifier is required.';
      return;
    }
    if (basePurposes.some((p) => p.id === id) && stagedEditsState.purpose_edits[id]?.action !== 'remove') {
      addError = `Identifier "${id}" is already in use.`;
      return;
    }
    if (!display_name) {
      addError = 'Display name is required.';
      return;
    }
    const minRaw = newPurpose.min.trim();
    const maxRaw = newPurpose.max.trim();
    if (!/^\d+$/.test(minRaw) || !/^\d+$/.test(maxRaw)) {
      addError = 'Amount range minimum and maximum are required (whole-dollar integers).';
      return;
    }
    const purpose: LoanPurpose = {
      id,
      display_name,
      amount_range: { min: Number(minRaw), max: Number(maxRaw) }
    };
    if (url_slug) purpose.url_slug = url_slug;
    const validationMessage = purposeAmountErrorMessage(purpose);
    if (validationMessage) {
      addError = validationMessage;
      return;
    }
    stageAddPurpose(purpose);
    newPurpose = { id: '', display_name: '', url_slug: '', min: '', max: '' };
    addOpen = false;
  }

  const stagedCount = $derived(Object.keys(stagedEditsState.purpose_edits).length);
</script>

<section class="purposes-editor">
  {#if locked && stagedEditsState.valueType}
    <ValueTypeLockBanner
      currentValueType={stagedEditsState.valueType}
      attemptedValueType="purposes"
    />
  {/if}

  <p class="muted">Per-purpose amount ranges must fit inside the product's loan amount range (${formatAmount(productRange.min)} – ${formatAmount(productRange.max)}). Identifiers are stable references and cannot be changed once a purpose exists.</p>

  <table class="purposes-table" class:is-locked={locked}>
    <thead>
      <tr>
        <th scope="col">ID</th>
        <th scope="col">Display name</th>
        <th scope="col">URL slug</th>
        <th scope="col" class="num">Min</th>
        <th scope="col" class="num">Max</th>
        <th scope="col">Status</th>
        <th scope="col" class="actions">Actions</th>
      </tr>
    </thead>
    <tbody>
      {#each rows as row (row.purpose.id)}
        {@const errorMessage = purposeAmountErrorMessage(row.purpose)}
        <tr class="status-{row.status}">
          <td><code>{row.purpose.id}</code></td>
          <td>
            <input
              type="text"
              aria-label="Display name for {row.purpose.id}"
              value={row.purpose.display_name ?? ''}
              disabled={locked || row.status === 'removed'}
              oninput={(e) => updatePurposeField(row, 'display_name', (e.target as HTMLInputElement).value)}
            />
          </td>
          <td>
            <input
              type="text"
              aria-label="URL slug for {row.purpose.id}"
              value={row.purpose.url_slug ?? ''}
              disabled={locked || row.status === 'removed'}
              oninput={(e) => updatePurposeField(row, 'url_slug', (e.target as HTMLInputElement).value)}
            />
          </td>
          <td class="num">
            <input
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              aria-label="Minimum amount for {row.purpose.id}"
              value={row.purpose.amount_range?.min ?? ''}
              disabled={locked || row.status === 'removed'}
              oninput={(e) => updatePurposeField(row, 'min', (e.target as HTMLInputElement).value)}
            />
          </td>
          <td class="num">
            <input
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              aria-label="Maximum amount for {row.purpose.id}"
              value={row.purpose.amount_range?.max ?? ''}
              disabled={locked || row.status === 'removed'}
              oninput={(e) => updatePurposeField(row, 'max', (e.target as HTMLInputElement).value)}
            />
          </td>
          <td>
            {#if row.status === 'added'}
              <span class="pill pill-added">Added</span>
            {:else if row.status === 'updated'}
              <span class="pill pill-updated">Updated</span>
            {:else if row.status === 'removed'}
              <span class="pill pill-removed">Removed</span>
            {/if}
            {#if errorMessage && row.status !== 'removed'}
              <span class="field-error">{errorMessage}</span>
            {/if}
          </td>
          <td class="actions">
            {#if row.status === 'removed'}
              <button class="btn-link" type="button" disabled={locked} onclick={() => restorePurpose(row)}>Restore</button>
            {:else if row.status === 'added'}
              <button class="btn-link" type="button" disabled={locked} onclick={() => removePurpose(row)}>Discard new</button>
            {:else if row.status === 'updated'}
              <button class="btn-link" type="button" disabled={locked} onclick={() => restorePurpose(row)}>Revert</button>
              <button class="btn-link" type="button" disabled={locked} onclick={() => removePurpose(row)}>Remove</button>
            {:else}
              <button class="btn-link" type="button" disabled={locked} onclick={() => removePurpose(row)}>Remove</button>
            {/if}
          </td>
        </tr>
      {/each}
    </tbody>
  </table>

  {#if !addOpen}
    <button class="btn-link" type="button" disabled={locked} onclick={() => { addOpen = true; addError = null; }}>+ Add a new loan purpose</button>
  {:else}
    <fieldset class="add-purpose">
      <legend>Add a new loan purpose</legend>
      <label>
        <span>Identifier *</span>
        <input type="text" bind:value={newPurpose.id} placeholder="e.g. 19" />
      </label>
      <label>
        <span>Display name *</span>
        <input type="text" bind:value={newPurpose.display_name} placeholder="e.g. Renovation" />
      </label>
      <label>
        <span>URL slug</span>
        <input type="text" bind:value={newPurpose.url_slug} placeholder="e.g. renovation-loan" />
      </label>
      <label>
        <span>Minimum amount ($) *</span>
        <input type="text" inputmode="numeric" pattern="[0-9]*" bind:value={newPurpose.min} />
      </label>
      <label>
        <span>Maximum amount ($) *</span>
        <input type="text" inputmode="numeric" pattern="[0-9]*" bind:value={newPurpose.max} />
      </label>
      {#if addError}
        <p class="banner banner-fail">{addError}</p>
      {/if}
      <div class="add-purpose__actions">
        <button class="btn-link" type="button" onclick={() => { addOpen = false; addError = null; }}>Cancel</button>
        <button class="btn" type="button" onclick={submitNewPurpose}>Add purpose</button>
      </div>
    </fieldset>
  {/if}

  {#if !locked && stagedCount > 0}
    <div class="purposes-editor__actions">
      <span class="muted">{stagedCount} purpose change{stagedCount === 1 ? '' : 's'} staged</span>
      <button class="btn btn-primary" type="button" onclick={openSubmit}>Submit for review →</button>
    </div>
  {/if}
</section>

{#if submitOpen}
  <SubmitForReview
    {catalogue}
    valueType="purposes"
    onClose={closeSubmit}
    onSubmitted={onProposalCreated}
  />
{/if}

<style>
  .purposes-editor {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .purposes-table {
    width: 100%;
    border-collapse: collapse;
  }
  /* Mute the rest of the editor surface when locked so the
     ValueTypeLockBanner's Discard affordance is the obvious next
     action. */
  .purposes-table.is-locked {
    opacity: 0.55;
  }
  .purposes-table th,
  .purposes-table td {
    padding: var(--space-2) var(--space-3);
    border-bottom: 1px solid var(--surface-line-soft);
    text-align: left;
    vertical-align: top;
  }
  .purposes-table th.num,
  .purposes-table td.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
    font-family: var(--font-mono);
  }
  .purposes-table th.actions,
  .purposes-table td.actions {
    text-align: right;
  }
  .purposes-table input {
    padding: var(--space-1) var(--space-2);
    width: 100%;
    box-sizing: border-box;
  }
  .purposes-table td.num input {
    text-align: right;
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    width: 7rem;
  }
  .purposes-table tr.status-removed {
    opacity: 0.55;
    text-decoration: line-through;
  }
  .purposes-table tr.status-added,
  .purposes-table tr.status-updated {
    background: var(--surface-highlight, transparent);
  }
  .pill {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: var(--type-xs);
    font-weight: 500;
  }
  .pill-added { background: var(--status-pass-soft, #d9f7d4); color: var(--status-pass, #1c4f1c); }
  .pill-updated { background: var(--status-warn-soft, #fff4d4); color: var(--status-warn, #6b4f00); }
  .pill-removed { background: var(--status-fail-soft, #fdd); color: var(--status-fail, #8a1c1c); }
  .field-error {
    display: block;
    color: var(--status-fail, #b00020);
    font-size: var(--type-xs);
    margin-top: var(--space-1);
  }
  .add-purpose {
    border: 1px solid var(--surface-line);
    padding: var(--space-3);
    border-radius: var(--radius-sm);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .add-purpose label { display: flex; flex-direction: column; gap: var(--space-1); font-size: var(--type-sm); }
  .add-purpose__actions { display: flex; gap: var(--space-2); justify-content: flex-end; }
  .purposes-editor__actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: var(--space-2);
  }
</style>
